import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import {
  Grid3X3, Plus, X, Loader2, Sparkles, AlertTriangle, Lightbulb,
  ArrowUpRight, ArrowDownRight, DollarSign, ShieldAlert,
} from "lucide-react";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPACT_COLORS = {
  high: "text-[hsl(var(--success))]",
  medium: "text-[hsl(var(--warning))]",
  low: "text-muted-foreground",
};

const EFFORT_COLORS = {
  low: "text-[hsl(var(--success))]",
  medium: "text-[hsl(var(--warning))]",
  high: "text-destructive",
};

const getMatrixQuadrant = (impact, effort) => {
  if (impact === "high" && effort === "low") return "quick-win";
  if (impact === "high" && effort === "high") return "major-project";
  if (impact === "low" && effort === "low") return "fill-in";
  return "thankless";
};

export default function SolutionsTab({ projectId, token, solutions, setSolutions, drStatus }) {
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  const addSolution = () => {
    setSolutions(prev => [...prev, { solution_id: "", name: "", description: "", impact: "medium", effort: "medium", cost: "", timeline: "", owner: "", status: "proposed" }]);
  };

  const updateSolution = (index, field, value) => {
    setSolutions(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const removeSolution = (index) => {
    if (solutions.length <= 1) return;
    setSolutions(prev => prev.filter((_, i) => i !== index));
  };

  const saveSolutions = async () => {
    setSaving(true);
    try {
      const cleaned = solutions.filter(s => s.name.trim()).map(s => ({
        ...s,
        cost: s.cost === "" || s.cost === null || s.cost === undefined ? null : Number(s.cost),
        timeline: s.timeline === "" ? null : s.timeline,
      }));
      const response = await fetch(`${API}/projects/${projectId}/improve/solutions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ solutions: cleaned }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail?.[0]?.msg || err.detail || "Failed to save");
      }
      toast.success("Solutions saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const suggestSolutions = async () => {
    setSuggesting(true);
    try {
      const fishboneRes = await fetch(`${API}/projects/${projectId}/fishbone`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let rootCauses = [];
      if (fishboneRes.ok) {
        const fishbones = await fishboneRes.json();
        for (const fb of fishbones) {
          for (const cat of fb.categories || []) {
            rootCauses.push(...(cat.causes || []));
          }
        }
      }
      const response = await fetch(`${API}/ai/suggest-solutions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId, root_causes: rootCauses, existing: solutions.filter(s => s.name.trim()) }),
      });
      if (!response.ok) throw new Error("AI suggestion failed");
      const data = await response.json();
      if (data.solutions) {
        setSolutions(prev => [
          ...prev.filter(s => s.name.trim()),
          ...data.solutions.map(s => ({
            solution_id: "", name: s.name,
            description: (s.description || "") + (s.root_cause_link ? `\n[Root Cause: ${s.root_cause_link}]` : "") + (s.rationale ? `\n[Rationale: ${s.rationale}]` : ""),
            impact: s.impact || "medium", effort: s.effort || "medium",
            cost: s.cost_category === "high" ? "50000" : s.cost_category === "medium" ? "25000" : s.cost_category === "low" ? "5000" : "",
            timeline: s.timeline || "", owner: "", status: "proposed"
          }))
        ]);
        toast.success(`${data.solutions.length} AI suggestions added`);
        if (data.pilot_plan) toast.info(`Recommended pilot: ${data.pilot_plan.duration_weeks} weeks, ${data.pilot_plan.sample_size}`);
      }
      if (data.ai_commentary) toast(data.ai_commentary, { duration: 5000 });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSuggesting(false);
    }
  };

  const analyzeSolutions = async () => {
    setAnalyzing(true);
    try {
      const response = await fetch(`${API}/ai/analyze-solutions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ solutions: solutions.filter(s => s.name.trim()) }),
      });
      if (!response.ok) throw new Error("Analysis failed");
      const data = await response.json();
      setAiAnalysis(data);
      if (data.alignment_issues?.length > 0) toast.warning(`Found ${data.alignment_issues.length} alignment issue(s)`);
      else toast.success("Solutions are well-aligned!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      {drStatus === "exploratory_only" && (
        <div className="bg-orange-400/10 border border-orange-400/30 rounded-lg p-4 flex items-center gap-3" data-testid="exploratory-warning">
          <ShieldAlert className="w-5 h-5 text-orange-400 shrink-0" />
          <p className="text-sm font-medium text-orange-400">Exploratory only — do not deploy. All solutions entered here are for brainstorming purposes only.</p>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold">Solution Prioritization Matrix</h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={analyzeSolutions} disabled={analyzing} className="gap-2">
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              Analyze
              <CreditCostTag cost={5} />
            </Button>
            <Button variant="outline" size="sm" onClick={suggestSolutions} disabled={suggesting} className="gap-2" data-testid="suggest-solutions-btn">
              {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              AI Suggest
              <CreditCostTag cost={5} />
            </Button>
            <Button variant="outline" size="sm" onClick={addSolution}>
              <Plus className="w-4 h-4 mr-1" /> Add Solution
            </Button>
          </div>
        </div>

        {/* AI Analysis Results */}
        {aiAnalysis && (
          <div className="mb-6 space-y-4">
            {aiAnalysis.alignment_issues?.length > 0 && (
              <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                <h4 className="font-medium text-destructive mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Alignment Issues
                </h4>
                <ul className="space-y-1 text-sm">
                  {aiAnalysis.alignment_issues.map((issue, i) => (
                    <li key={i} className="text-muted-foreground"><strong>{issue.solution}:</strong> {issue.issue}</li>
                  ))}
                </ul>
              </div>
            )}
            {aiAnalysis.recommendations?.length > 0 && (
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <h4 className="font-medium text-primary mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> AI Recommendations
                </h4>
                <ul className="space-y-1 text-sm">
                  {aiAnalysis.recommendations.map((rec, i) => (
                    <li key={i} className="text-muted-foreground">{rec}</li>
                  ))}
                </ul>
              </div>
            )}
            {aiAnalysis.projected_savings && (
              <div className="p-4 bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-lg">
                <h4 className="font-medium text-[hsl(var(--success))] mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Projected Financial Impact
                </h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Investment</p><p className="font-bold">${aiAnalysis.projected_savings.total_investment?.toLocaleString()}</p></div>
                  <div><p className="text-muted-foreground">Annual Savings</p><p className="font-bold">${aiAnalysis.projected_savings.annual_savings?.toLocaleString()}</p></div>
                  <div><p className="text-muted-foreground">5-Year Impact</p><p className="font-bold text-[hsl(var(--success))]">${aiAnalysis.projected_savings.five_year_impact?.toLocaleString()}</p></div>
                  <div><p className="text-muted-foreground">Overall ROI</p><p className="font-bold text-[hsl(var(--success))]">{aiAnalysis.projected_savings.overall_roi}%</p></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Matrix Visualization */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-lg p-4">
            <h4 className="font-medium text-sm text-[hsl(var(--success))] mb-2 flex items-center gap-1">
              <ArrowUpRight className="w-4 h-4" /> Quick Wins (High Impact, Low Effort)
            </h4>
            <div className="space-y-1">
              {solutions.filter(s => getMatrixQuadrant(s.impact, s.effort) === "quick-win" && s.name).map((s, i) => (
                <div key={i} className="text-sm bg-background/50 rounded px-2 py-1">{s.name}</div>
              ))}
            </div>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h4 className="font-medium text-sm text-primary mb-2 flex items-center gap-1">
              <Lightbulb className="w-4 h-4" /> Major Projects (High Impact, High Effort)
            </h4>
            <div className="space-y-1">
              {solutions.filter(s => getMatrixQuadrant(s.impact, s.effort) === "major-project" && s.name).map((s, i) => (
                <div key={i} className="text-sm bg-background/50 rounded px-2 py-1">{s.name}</div>
              ))}
            </div>
          </div>
          <div className="bg-muted border border-border rounded-lg p-4">
            <h4 className="font-medium text-sm text-muted-foreground mb-2">Fill-Ins (Low Impact, Low Effort)</h4>
            <div className="space-y-1">
              {solutions.filter(s => getMatrixQuadrant(s.impact, s.effort) === "fill-in" && s.name).map((s, i) => (
                <div key={i} className="text-sm bg-background/50 rounded px-2 py-1">{s.name}</div>
              ))}
            </div>
          </div>
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
            <h4 className="font-medium text-sm text-destructive mb-2 flex items-center gap-1">
              <ArrowDownRight className="w-4 h-4" /> Avoid (Low Impact, High Effort)
            </h4>
            <div className="space-y-1">
              {solutions.filter(s => getMatrixQuadrant(s.impact, s.effort) === "thankless" && s.name).map((s, i) => (
                <div key={i} className="text-sm bg-background/50 rounded px-2 py-1">{s.name}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Solutions List */}
        <div className="space-y-4">
          {solutions.map((solution, index) => (
            <div key={index} className="p-4 bg-background border border-border rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Solution Name</Label>
                  <Input value={solution.name} onChange={(e) => updateSolution(index, "name", e.target.value)} placeholder="Solution name" data-testid={`solution-name-${index}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Impact</Label>
                  <select value={solution.impact} onChange={(e) => updateSolution(index, "impact", e.target.value)} className={`w-full h-10 px-3 rounded-md border border-input bg-background text-sm ${IMPACT_COLORS[solution.impact]}`} data-testid={`solution-impact-${index}`}>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Effort</Label>
                  <select value={solution.effort} onChange={(e) => updateSolution(index, "effort", e.target.value)} className={`w-full h-10 px-3 rounded-md border border-input bg-background text-sm ${EFFORT_COLORS[solution.effort]}`} data-testid={`solution-effort-${index}`}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Owner</Label>
                  <Input value={solution.owner} onChange={(e) => updateSolution(index, "owner", e.target.value)} placeholder="Owner" />
                </div>
                <div className="flex items-end">
                  {solutions.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeSolution(index)} className="text-destructive">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-3">
                <Textarea value={solution.description} onChange={(e) => updateSolution(index, "description", e.target.value)} placeholder="Description..." rows={2} />
              </div>
            </div>
          ))}
        </div>

        <Button onClick={saveSolutions} disabled={saving} className="mt-4" data-testid="save-solutions-btn">
          {saving ? "Saving..." : "Save Solutions"}
        </Button>

        {solutions.some(s => s.name) && (
          <ArtifactQualityReview projectId={projectId} artifactType="solution_plan" token={token} className="mt-6" />
        )}
      </div>
    </div>
  );
}
